import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { Template } from "../types/template";
import { request } from "../../../services/cpln";
import { Loader } from "../../../components/layout/loader";
import { getMarketplaceErrorMessage, getTemplateVersion, getVersions } from "../utils";
import { MARKETPLACE_BRANCH, MARKETPLACE_SERVICE_URL, MARKETPLACE_TEMPLATE_REPO } from "../../../envVariables";
import { MarketplaceIcon } from "../components/MarketplaceIcon";
import { Alert, Button, Tag } from "antd";
import { useAppSelector } from "../../../store/hooks";

type RouteParams = "template";
export const MarketplaceTemplateDetail: React.FC = () => {
  const theme = useAppSelector((store) => store.theme.theme);

  const { template: templateName } = useParams<RouteParams>();

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [template, setTemplate] = React.useState<Template>(null as any);
  const versions = getVersions(template);

  React.useEffect(() => {
    fetchTemplate();
  }, []);

  async function fetchTemplate() {
    try {
      setIsLoading(true);
      setError("");
      const { data } = await request({ service: "marketplace", url: `/template/${templateName}` });
      setTemplate(data);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      setError(Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage);
    }
  }

  const navigate = useNavigate();

  const [version, setVersion] = React.useState("");
  const templateVersion = getTemplateVersion(template, version);

  React.useEffect(() => {
    if (!template) {
      return;
    }

    setVersion(versions[versions.length - 1]);
  }, [template]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <Alert type={"error"} message="Error" description={error} />;
  }

  return (
    <div className="template-detail h-full">
      <div className="template-detail-card h-full rounded p-4">
        <div className="flex justify-between items-start mb-8">
          <div
            className="flex items-center px-4 py-1 rounded"
            style={{ color: "var(--color-gray-1600)", backgroundColor: "white" }}
          >
            {template.hasIcon !== false && (
              <div style={{ width: 50, height: "100%" }}>
                <MarketplaceIcon templateName={templateName!} hasIcon={template.hasIcon} className="object-fit" />
              </div>
            )}
            <div className="flex flex-col ml-4">
              <span className="leading-none text-lg font-semibold">{template.name}</span>
              <span className="leading-none text-sm">{templateVersion?.category}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div>
              <Tag>{`Template ${versions[versions.length - 1]}`}</Tag>
            </div>
            <div>
              <Tag>{`App v${templateVersion?.appVersion}`}</Tag>
            </div>
          </div>
        </div>
        <div className="markdown h-full">
          <iframe
            src={`${MARKETPLACE_SERVICE_URL}/template/${template.name}/${version}/readme${
              theme === "dark" ? "?theme=dark" : ""
            }`}
            allow="clipboard-write"
            width="100%"
            height="90%"
          />
        </div>
      </div>
      <div className="template-detail-card rounded p-4">
        <Button className="mb-4" type="primary" onClick={() => navigate("install")}>
          Create Template Release
        </Button>
        <div className="divider" />
        <div className="mt-2">Template Info</div>
        <ExternalLink
          url={`${MARKETPLACE_TEMPLATE_REPO}/${MARKETPLACE_BRANCH}/${template.name}`}
          label="Repository"
          disableCopy
        />
      </div>
    </div>
  );
};
